import { useEffect, useMemo, useState } from "react";
import { Bar, Bubble, Line, Pie, Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  BubbleController,
  ScatterController,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { ChatMessage, MessageMetadata } from "@racoon/api";
import { fmt, DataTable, type Column, Separator } from "@racoon/ui";
import { IcCancel } from "@racoon/icons";

interface ChartDataset {
  label: string;
  data: number[] | Array<{ x: number; y: number; r?: number }>;
  stack?: string | null;
  yAxisID?: string | null;
  order?: number;
}

interface ChartConfig {
  type: "area" | "bar" | "bubble" | "line" | "pie" | "scatter";
  title: string;
  labels: string[];
  datasets: ChartDataset[];
  indexAxis?: "x" | "y";
  stacked?: boolean;
}

export type MessageWithCharts = ChatMessage & { charts?: ChartConfig[]; timestamp: string };

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  BubbleController,
  ScatterController,
  Filler,
  Tooltip,
  Legend,
);

// Helper: detect if a value is a date string
function isDate(val: any) {
  if (typeof val !== "string") return false;
  return /^\d{4}-\d{2}-\d{2}/.test(val) || !isNaN(Date.parse(val));
}

// Helper: generate a color palette
function getColorPalette(n: number) {
  const palette = [
    "#4285F4",
    "#EA4335",
    "#FBBC05",
    "#34A853",
    "#A142F4",
    "#F44292",
    "#00B8D9",
    "#FF5630",
    "#36B37E",
    "#FFAB00",
    "#6554C0",
    "#FF8B00",
  ];
  return Array.from({ length: n }, (_, i) => palette[i % palette.length]);
}

// Helper: smartly format MessageMetadata for Chart.js
function smartMetadataToChartData(metadata?: MessageMetadata) {
  if (!metadata || !metadata.columns || !metadata.rows) return null;

  const isNumeric = (val: any) => !isNaN(parseFloat(val)) && isFinite(val);

  if (metadata.columns.length === 1 && metadata.rows.length === 1) {
    const col = metadata.columns[0];
    const val = metadata.rows[0][col];
    return {
      labels: [col],
      datasets: [
        {
          label: col,
          data: [isNumeric(val) ? Number(val) : 0],
          backgroundColor: getColorPalette(1)[0],
        },
      ],
    };
  }

  const labelCol =
    metadata.columns.find((col) => !isNumeric(metadata.rows[0]?.[col])) || metadata.columns[0];

  const valueCols = metadata.columns.filter(
    (col) => col !== labelCol && isNumeric(metadata.rows[0]?.[col]),
  );
  const datasetsCols = valueCols.length
    ? valueCols
    : metadata.columns.filter((col) => col !== labelCol);

  const labels = metadata.rows.map((row) => {
    const val = row[labelCol];
    if (isDate(val)) return fmt.date(val);
    if (typeof val === "string" && val.length > 40) return val.slice(0, 37) + "...";
    return val;
  });

  const datasets = datasetsCols.map((col, i) => ({
    label: col,
    data: metadata.rows.map((row) => {
      const val = row[col];
      return isNumeric(val) ? Number(val) : null;
    }),
    backgroundColor: getColorPalette(datasetsCols.length)[i],
  }));

  return { labels, datasets };
}

export const ChatChart = ({
  message,
  onClose,
}: {
  message?: MessageWithCharts | null;
  onClose: () => void;
}) => {
  const currentMessage = message;
  const charts = currentMessage?.charts || [];
  const hasMultipleCharts = charts.length > 1;

  const [activeChartIdx, setActiveChartIdx] = useState(0);
  const [chartType, setChartType] = useState<string>(charts[0]?.type || "bar");
  const [showTable] = useState(false);
  const [, setDashboardTitle] = useState(currentMessage?.metadata?.plot_title || "Dashboard");

  useEffect(() => {
    setActiveChartIdx(0);
    const chart = message?.charts?.[0];
    setChartType(chart?.type || "bar");
    setDashboardTitle(message?.metadata?.plot_title || "Dashboard");
  }, [message]);

  const activeChart = charts[activeChartIdx] ?? null;

  const handleChartTabChange = (idx: number) => {
    setActiveChartIdx(idx);
    setChartType(charts[idx]?.type || "bar");
  };

  const chartData = activeChart
    ? {
        labels: activeChart.labels,
        datasets: activeChart.datasets.map((ds, i) => ({
          label: ds.label,
          data: ds.data,
          backgroundColor: getColorPalette(activeChart.datasets.length)[i],
        })),
      }
    : smartMetadataToChartData(currentMessage?.metadata);

  const baseOptions: Record<string, any> = {
    maintainAspectRatio: false,
    ...(activeChart?.indexAxis ? { indexAxis: activeChart.indexAxis } : {}),
    ...(activeChart?.stacked ? { scales: { x: { stacked: true }, y: { stacked: true } } } : {}),
  };

  // Legend rendering
  const renderLegend = () => {
    if (!chartData || !chartData.datasets) return null;
    return (
      <div className="flex gap-4 justify-center mb-2 flex-wrap">
        {chartData.datasets.map((ds) => (
          <div key={ds.label} className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: ds.backgroundColor as string }}
            />
            <span className="text-xs font-medium" style={{ color: ds.backgroundColor as string }}>
              {ds.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Table rendering (raw HTML)
  const renderTable = () => {
    if (!message?.metadata) return null;
    const { columns, rows } = message.metadata;
    if (!columns.length || !rows.length) return <div>No data</div>;
    return (
      <div className="overflow-auto max-h-96">
        <table className="min-w-full border">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => {
                  let val = row[col];
                  if (isDate(val)) val = fmt.date(val);
                  if (typeof val === "string" && val.length > 60)
                    return (
                      <td key={col} title={val}>
                        {val.slice(0, 57) + "..."}
                      </td>
                    );
                  return <td key={col}>{val}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderChart = () => {
    if (!chartData) return <div className="text-gray-400 text-sm">No chart data available.</div>;

    const colors = getColorPalette(chartData.datasets.length);

    switch (chartType) {
      case "bar": {
        const data = {
          labels: chartData.labels,
          datasets: chartData.datasets.map((ds, i) => ({
            ...ds,
            backgroundColor: colors[i],
          })),
        };
        return <Bar data={data as any} options={baseOptions} />;
      }

      case "line": {
        const data = {
          labels: chartData.labels,
          datasets: chartData.datasets.map((ds, i) => ({
            ...ds,
            backgroundColor: colors[i],
            borderColor: colors[i],
          })),
        };
        return <Line data={data as any} options={baseOptions} />;
      }

      case "area": {
        const data = {
          labels: chartData.labels,
          datasets: chartData.datasets.map((ds, i) => ({
            ...ds,
            backgroundColor: colors[i] + "44",
            borderColor: colors[i],
            fill: true,
          })),
        };
        return <Line data={data as any} options={baseOptions} />;
      }

      case "scatter": {
        const data = {
          datasets: chartData.datasets.map((ds, i) => ({
            label: ds.label,
            data: (ds.data as any[]).map((v, j) =>
              typeof v === "object" && v !== null ? v : { x: j, y: v },
            ),
            backgroundColor: colors[i],
          })),
        };
        return <Scatter data={data} options={baseOptions} />;
      }

      case "bubble": {
        const data = {
          datasets: chartData.datasets.map((ds, i) => ({
            label: ds.label,
            data: (ds.data as any[]).map((v, j) =>
              typeof v === "object" && v !== null ? v : { x: j, y: v, r: 6 },
            ),
            backgroundColor: colors[i] + "99",
            borderColor: colors[i],
          })),
        };
        return <Bubble data={data} options={baseOptions} />;
      }

      case "pie": {
        const pieColors = getColorPalette(chartData.labels.length);
        return (
          <Pie
            data={{
              labels: chartData.labels,
              datasets: [
                {
                  label: chartData.datasets[0]?.label,
                  data: chartData.datasets[0]?.data as number[],
                  backgroundColor: pieColors,
                },
              ],
            }}
            options={{ maintainAspectRatio: false }}
          />
        );
      }

      case "table":
        return renderTable();

      default:
        return null;
    }
  };

  // DataTable columns and data
  const tableColumns: Column<any>[] = useMemo(() => {
    if (!currentMessage?.metadata?.columns) return [];
    return currentMessage.metadata.columns.map((col) => ({
      id: col,
      header: col,
      key: col,
      sortable: true,
    }));
  }, [currentMessage]);
  const tableData = currentMessage?.metadata?.rows || [];

  const renderDataTable = () => (
    <div className="w-full h-[400px]">
      <DataTable columns={tableColumns} data={tableData} />
    </div>
  );

  return (
    <div className="flex flex-col gap-4 w-full h-full bg-white dark:bg-black rounded-xl p-4 relative">
      {/* Header */}
      <div className="flex justify-between items-center gap-2">
        <h3 className="font-semibold">{charts[activeChartIdx]?.title || "No Title"}</h3>
        <IcCancel className="w-6 h-6 cursor-pointer" onClick={onClose} />
      </div>

      <Separator className="-mx-4 !w-[calc(100%+32px)]" />

      {/* Chart tabs */}
      {hasMultipleCharts && (
        <div className="flex gap-1 flex-wrap">
          {charts.map((chart, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleChartTabChange(idx)}
              title={chart.title || `График ${idx + 1}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors truncate max-w-[150px] ${
                idx === activeChartIdx
                  ? "bg-brand-blue text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {chart.title || `График ${idx + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* Legend */}
      {chartType !== "table" && renderLegend()}

      {/* Chart/Table Area */}
      <div className="flex-1 flex items-center justify-center min-h-[300px]">
        {showTable ? renderDataTable() : renderChart()}
      </div>

      {/* Bottom Controls */}
      {/* <div className="flex items-center justify-between mt-4 text-xs text-gray-400">
        <Select value={chartType} onValueChange={setChartType}>
          <SelectTrigger id="chat-chart-type-select" className="w-36">
            <SelectValue placeholder="Chart type" />
          </SelectTrigger>
          <SelectContent>
            {chartTypes.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          id="chat-chart-download-button"
          variant="ghost"
          size="sm"
          className="opacity-80"
          onClick={handleDownloadExcel}
          disabled={!tableColumns.length || !tableData.length}
        >
          Download as
        </Button>
        <Button
          id="chat-chart-toggle-view-button"
          variant="ghost"
          size="sm"
          className="opacity-80"
          onClick={() => setShowTable((v) => !v)}
          disabled={!tableColumns.length || !tableData.length}
        >
          {showTable ? "Show as chart" : "Show as table"}
        </Button>
      </div> */}
    </div>
  );
};
